/**
 * Utility functions for the Advanced Retirement Calculator
 * @module domain/retirement/utils
 */

import type { Fiat } from "~/types/global.types";
import type { LifeEvent, LifeEventScenario } from "./types";

// ============================================================================
// CURRENCY CONVERSION
// ============================================================================

/**
 * Convert an amount from one currency to ILS
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency object or code
 * @param fiatData - Array of fiat currencies with rates
 * @returns Amount in ILS
 */
export function convertToBaseCurrency(
  amount: number,
  fromCurrency: Fiat | { value?: string; rate?: number } | string | undefined,
  fiatData: Fiat[]
): number {
  // Handle zero or undefined amount
  if (!amount) return 0;
  
  // If no currency specified, assume ILS (no conversion needed)
  if (!fromCurrency) return amount;

  // Get the currency code and rate from the input
  let currencyCode: string;
  let currencyRate: number | undefined;

  if (typeof fromCurrency === "string") {
    currencyCode = fromCurrency;
  } else if (fromCurrency && typeof fromCurrency === "object") {
    // Handle Fiat object or similar structure
    currencyCode = fromCurrency.value || "ILS";
    currencyRate = fromCurrency.rate;
  } else {
    currencyCode = "ILS";
  }

  // If already in ILS, return as is
  if (currencyCode === "ILS") return amount;

  // Try to get rate from the currency object first
  if (currencyRate && currencyRate > 0) {
    return amount * currencyRate;
  }

  // Look up the rate in fiatData
  const currencyInfo = fiatData.find(f => f.value === currencyCode);
  if (currencyInfo && currencyInfo.rate && currencyInfo.rate > 0) {
    return amount * currencyInfo.rate;
  }

  // If we can't find a rate, return the original amount
  // (better than returning 0 or crashing)
  console.warn(`Could not find exchange rate for ${currencyCode}, returning original amount`);
  return amount;
}

// ============================================================================
// OWNERSHIP CALCULATION
// ============================================================================

/**
 * Apply ownership percentage to a value
 */
export function applyOwnership(value: number, ownership?: number | null): number {
  if (ownership === undefined || ownership === null || ownership === 100) {
    return value;
  }
  return value * (ownership / 100);
}

// ============================================================================
// DATE/AGE UTILITIES
// ============================================================================

/**
 * Calculate age from birth date
 */
export function calculateAge(birthDate: string | Date): number {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

// ============================================================================
// ID GENERATION
// ============================================================================

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// SCENARIO HELPERS
// ============================================================================

/**
 * Create a new scenario with a given name and optional events.
 */
export function createScenario(name: string, events: LifeEvent[] = [], isActive = false): LifeEventScenario {
  return { id: generateId(), name, events, isActive };
}

/**
 * Get the active scenario from a list. Falls back to the first one.
 */
export function getActiveScenario(scenarios: LifeEventScenario[]): LifeEventScenario | null {
  if (scenarios.length === 0) return null;
  return scenarios.find((s) => s.isActive) ?? scenarios[0];
}

/**
 * Get life events from the active scenario.
 */
export function getActiveScenarioEvents(scenarios: LifeEventScenario[]): LifeEvent[] {
  const active = getActiveScenario(scenarios);
  return active ? active.events : [];
}

/**
 * Migrate a flat lifeEvents array into a single default scenario.
 */
export function migrateToScenarios(lifeEvents: LifeEvent[]): LifeEventScenario[] {
  return [createScenario("תרחיש ראשי", lifeEvents, true)];
}
